import {
  type Activity,
  type Gear,
  type Route,
  TimeRange,
} from '@strava-heatmapper/shared/interfaces';
import localforage from 'localforage';

import { type FilterField, type FilterModel } from '@/types/FilterModel';

export interface StoreMeta {
  version: number;
  user: number;
}

export interface ActivityStore {
  covered: TimeRange[];
  activities: Activity[];
}

export interface RouteStore {
  routes: Route[];
}

localforage.config({
  name: 'heatmapper',
});

const gearStore = localforage.createInstance({
  storeName: 'gear',
});

const activityStore = localforage.createInstance({
  storeName: 'activities',
});

const routeStore = localforage.createInstance({
  storeName: 'routes',
});

/** Per-store write queues, so read-modify-write ops on the same store can't interleave. */
const writeQueues = new WeakMap<LocalForage, Promise<unknown>>();

/**
 * Serialize a read-modify-write against a single store instance. Concurrent calls for the
 * same store run one at a time, in call order; calls for different stores run independently.
 */
function serializeStoreWrite<T>(store: LocalForage, op: () => Promise<T>): Promise<T> {
  const queue = writeQueues.get(store) ?? Promise.resolve();
  const result = queue.then(op);
  writeQueues.set(
    store,
    result.catch(() => undefined),
  ); // keep the chain alive even if op rejects
  return result;
}

export async function saveCachedGear(id: string, gear: Gear) {
  await gearStore.setItem(id, gear);
}

export async function getCachedGear(id: string): Promise<Gear | undefined> {
  const gear = await gearStore.getItem<Gear>(id);
  return gear ?? undefined;
}

export function loadFilterModel() {
  const json = localStorage.getItem('filter');
  if (json) return JSON.parse(json) as FilterModel;
}

export function saveFilterModel(filter: FilterModel) {
  localStorage.setItem('filter', JSON.stringify(filter));
}

export function loadFilterFields() {
  const json = localStorage.getItem('filterFields');
  if (json) return new Set(JSON.parse(json) as FilterField[]);
}

export function saveFilterFields(filter: Set<FilterField>) {
  localStorage.setItem('filterFields', JSON.stringify(Array.from(filter)));
}

// returns a string to be validated by the consumer
export function loadGroupLevel(): string | undefined {
  return localStorage.getItem('groupLevel') ?? undefined;
}

export function saveGroupLevel(level: string) {
  localStorage.setItem('groupLevel', level);
}

export interface StoredDisplayOptions {
  showActivities?: boolean;
}

export function loadDisplayOptions(): StoredDisplayOptions | undefined {
  const json = localStorage.getItem('displayOptions');
  if (json) return JSON.parse(json) as StoredDisplayOptions;
}

export function saveDisplayOptions(options: StoredDisplayOptions) {
  localStorage.setItem('displayOptions', JSON.stringify(options));
}

export function getStoreMeta(): StoreMeta {
  const rawCache = localStorage.getItem('meta');
  const cache = rawCache ? (JSON.parse(rawCache) as Partial<StoreMeta>) : undefined;
  return {
    version: cache?.version ?? 0,
    user: cache?.user ?? -1,
  };
}

export async function getActivityStore(): Promise<ActivityStore> {
  const cache = (await activityStore.getItem<Partial<ActivityStore>>('activities')) ?? undefined;
  return {
    covered: cache?.covered ?? [],
    activities: cache?.activities ?? [],
  };
}

async function setActivityStore(covered: TimeRange[], activities: Activity[]) {
  await activityStore.setItem<ActivityStore>('activities', { covered, activities });
}

export async function getRouteStore(): Promise<RouteStore> {
  const cache = (await routeStore.getItem<Partial<RouteStore>>('routes')) ?? undefined;
  return {
    routes: cache?.routes ?? [],
  };
}

export async function resetStore(version: number, user: number) {
  // Remove legacy localStorage items
  localStorage.removeItem('activities');
  localStorage.removeItem('routes');
  Object.keys(localStorage)
    .filter((key) => key.startsWith('gear:') || key.startsWith('map:'))
    .forEach((key) => localStorage.removeItem(key));
  localStorage.setItem('meta', JSON.stringify({ version, user } satisfies StoreMeta));
  await Promise.all([
    gearStore.clear(),
    serializeStoreWrite(activityStore, () => activityStore.clear()),
    serializeStoreWrite(routeStore, () => routeStore.clear()),
  ]);
}

export async function getCachedActivities(): Promise<Activity[]> {
  const store = await getActivityStore();
  return store.activities;
}

export async function getCachedRoutes(): Promise<Route[]> {
  const store = await getRouteStore();
  return store.routes;
}

export function appendCachedActivities(activities: Activity[], end: number, start?: number) {
  return serializeStoreWrite(activityStore, async () => {
    const existingStore = await getActivityStore();
    const ids = new Set(activities.map((activity) => activity.id));
    const newStore: ActivityStore = {
      covered: TimeRange.merge(existingStore.covered.concat({ start, end })),
      activities: existingStore.activities
        .filter((existingActivity) => !ids.has(existingActivity.id))
        .concat(activities)
        .sort((a, b) => b.date - a.date),
    };
    await setActivityStore(newStore.covered, newStore.activities);
  });
}

export function appendCachedRoutes(routes: Route[]) {
  return serializeStoreWrite(routeStore, async () => {
    const existingStore = await getRouteStore();
    const ids = new Set(routes.map((route) => route.id));
    const newStore: RouteStore = {
      routes: existingStore.routes
        .filter((existingRoute) => !ids.has(existingRoute.id))
        .concat(routes)
        .sort((a, b) => b.date - a.date),
    };
    await routeStore.setItem('routes', newStore);
  });
}

export function clearCachedActivities(start?: Date, end?: Date) {
  return serializeStoreWrite(activityStore, async () => {
    const store = await getActivityStore();
    if (!start && !end) {
      store.activities = [];
      store.covered = [];
    } else {
      const timeRange: TimeRange = {
        start: start && start.getTime() / 1000,
        end: end && end.getTime() / 1000,
      };
      store.activities = store.activities.filter(
        // activity.date is in milliseconds; TimeRange works in seconds.
        (activity) => !TimeRange.includes([timeRange], activity.date / 1000),
      );
      store.covered = TimeRange.subtract(store.covered, [timeRange]);
    }
    await setActivityStore(store.covered, store.activities);
  });
}

export function clearCachedRoutes() {
  return serializeStoreWrite(routeStore, () => routeStore.clear());
}

export async function getCachedGears(ids: string[]) {
  const notCached: string[] = [];

  const cached: Record<string, Gear> = {};
  for (const id of ids) {
    const fromCache = await getCachedGear(id);
    if (fromCache) cached[id] = fromCache;
    else notCached.push(id);
  }
  return { cached, notCached };
}
